import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CallIcon from "@mui/icons-material/Call";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../App";
import Color from "../../components/Color";

const AddCustomer = ({ open, onClose, formData, setFormData, onSubmit, phoneError }) => {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Sync backend phone error (duplicate or other validation from server)
  useEffect(() => {
    if (phoneError) {
      setErrors((prev) => ({
        ...prev,
        phone: [phoneError], // stored as array to match your existing pattern
      }));
    }
    // We do NOT auto-clear here — parent clears phoneError on success/reset
  }, [phoneError]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = ["Customer/company name is required"];
    } else if (formData.name.length > 50) {
      newErrors.name = ["Customer/company name must not exceed 50 characters"];
    }

    const phone = (formData.phone || "").trim();
    if (!phone) {
      newErrors.phone = ["Phone is required"];
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = ["Phone must be exactly 10 digits"];
    } else if (/^(\d)\1{9}$/.test(phone)) {
      newErrors.phone = ["Enter a valid mobile number"];
    }

    if (!formData.contact_type) {
      newErrors.contact_type = ["Contact type is required"];
    }

    if (formData.address && formData.address.length > 250) {
      newErrors.address = ["Address must not exceed 250 characters"];
    }

    if (formData.gst_number && formData.gst_number.length > 15) {
      newErrors.gst_number = ["GST number must not exceed 15 characters"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear client-side phone error before attempting submit
    // (backend error will come through phoneError prop → useEffect)
    setErrors((prev) => {
      const next = { ...prev };
      if (next.phone) delete next.phone;
      return next;
    });

    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      // Parent should handle success → resetForm() + close modal
    } catch (error) {
      // No need to set error here anymore — parent passes it via phoneError
      console.error("Submit failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      phone: "",
      address: "",
      gst_number: "",
      contact_type: "",
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const filtered = value.replace(/[^a-zA-Z0-9 &.,/-]/g, "");
      const limitedValue = filtered.slice(0, 50);

      setFormData((prev) => ({ ...prev, name: limitedValue }));

      if (limitedValue.length > 50) {
        setErrors((prev) => ({ ...prev, name: ["Customer/company name must not exceed 50 characters"] }));
      } else if (!limitedValue.trim()) {
        setErrors((prev) => ({ ...prev, name: ["Customer/company name is required"] }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "phone") {
      const numericValue = value.replace(/[^0-9]/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: numericValue }));

      if (numericValue.length === 10) {
        if (/^(\d)\1{9}$/.test(numericValue)) {
          setErrors((prev) => ({ ...prev, phone: ["Enter a valid mobile number"] }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.phone;
            return newErrors;
          });
        }
      } else if (numericValue.length > 0) {
        setErrors((prev) => ({ ...prev, phone: ["Phone must be exactly 10 digits"] }));
      } else {
        setErrors((prev) => ({ ...prev, phone: ["Phone is required"] }));
      }
    } else if (name === "address") {
      const limitedValue = value.slice(0, 250);
      setFormData((prev) => ({ ...prev, address: limitedValue }));

      if (value.length > 250) {
        setErrors((prev) => ({ ...prev, address: ["Address must not exceed 250 characters"] }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.address;
          return newErrors;
        });
      }
    } else if (name === "gst_number") {
      const formattedGst = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
      setFormData((prev) => ({ ...prev, gst_number: formattedGst }));

      if (formattedGst && formattedGst.length > 15) {
        setErrors((prev) => ({ ...prev, gst_number: ["GST number must not exceed 15 characters"] }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.gst_number;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (loading) return;
        handleCancel();
      }}
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: isMobile ? "1rem" : "1.25rem",
          position: "relative",
          pr: 6, // Space for close button
        }}
      >
        {formData.id ? "Edit Customer" : "Add Customer"}

        <IconButton
          aria-label="close"
          onClick={handleCancel}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "70vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            {/* Customer/Company Name */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Customer/Company Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                placeholder="Enter customer/company name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.[0] || `${formData.name?.length || 0}/50`}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Contact Type */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Contact Type*
              </Typography>
              <RadioGroup
                row
                name="contact_type"
                value={formData.contact_type || ""}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="whatsapp"
                  control={
                    <Radio
                      sx={{
                        color: Color.primary.main,
                        "&.Mui-checked": { color: Color.primary.main },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <WhatsAppIcon sx={{ fontSize: 18, color: "green" }} />
                      WhatsApp
                    </Box>
                  }
                />
                <FormControlLabel
                  value="mobile"
                  control={
                    <Radio
                      sx={{
                        color: Color.primary.main,
                        "&.Mui-checked": { color: Color.primary.main },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CallIcon sx={{ fontSize: 18 }} />
                      SMS
                    </Box>
                  }
                />
              </RadioGroup>
              {errors.contact_type && (
                <Typography color="error" variant="caption" display="block" mt={0.5}>
                  {errors.contact_type[0]}
                </Typography>
              )}
            </Box>

            {/* Phone – now shows both client-side and backend duplicate errors */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                {formData.contact_type === "whatsapp"
                  ? "WhatsApp Number*"
                  : formData.contact_type === "mobile"
                  ? "Mobile Number*"
                  : "Phone*"}
              </Typography>
              <TextField
                name="phone"
                value={formData.phone || ""}
                onChange={handleChange}
                placeholder={
                  formData.contact_type === "whatsapp"
                    ? "Enter WhatsApp number"
                    : formData.contact_type === "mobile"
                    ? "Enter mobile number"
                    : "Enter phone number"
                }
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.[0] || " "}  // ← space prevents layout jump
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                type="tel"
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.contact_type === "whatsapp" ? (
                        <WhatsAppIcon sx={{ color: "green" }} />
                      ) : formData.contact_type === "mobile" ? (
                        <CallIcon color="primary" />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* GST (Optional) */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                GST (Optional)
              </Typography>
              <TextField
                name="gst_number"
                value={formData.gst_number || ""}
                onChange={handleChange}
                placeholder="Enter GST number"
                fullWidth
                error={!!errors.gst_number}
                helperText={errors.gst_number?.[0] || `${formData.gst_number?.length || 0}/15`}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 15 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Address */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Address (Optional)
              </Typography>
              <TextField
                name="address"
                value={formData.address || ""}
                onChange={handleChange}
                placeholder="Enter address"
                fullWidth
                multiline
                rows={4}
                error={!!errors.address}
                helperText={errors.address?.[0] || `${formData.address?.length || 0}/250`}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 250 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": { background: Color.primary.dark },
                "&.Mui-disabled": {
                  background: Color.primary.main,
                  opacity: 0.7,
                  color: "white",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : formData.id ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomer;